import React from 'react';
import type { Permission } from '../../services/roleService';
import { Checkbox } from '../common/Checkbox';

interface PermissionMatrixProps {
  permissions: Permission[];
  selectedPermissions: number[];
  onChange: (permissionId: number, checked: boolean) => void;
  disabled?: boolean;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  selectedPermissions,
  onChange,
  disabled = false,
}) => {
  // Group permissions by extracting module from permission name (e.g., "leave_create" -> "leave")
  const groupedPermissions = permissions.reduce((acc, permission) => {
    // Try to extract module from permission name (assuming format: module_action)
    const parts = permission.name.split('_');
    const module = parts.length > 1 ? parts[0] : 'general';
    
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  const handlePermissionToggle = (permissionId: number) => {
    const isChecked = selectedPermissions.includes(permissionId);
    onChange(permissionId, !isChecked);
  };

  return (
    <div className="space-y-6">
      {Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)).map(([module, modulePermissions]) => (
        <div key={module} className="space-y-3">
          <h4 className="text-lg font-medium text-gray-900 capitalize">
            {module.replace(/_/g, ' ')}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modulePermissions.sort((a, b) => a.name.localeCompare(b.name)).map((permission) => (
              <div key={permission.id} className="flex items-start">
                <Checkbox
                  id={`permission-${permission.id}`}
                  checked={selectedPermissions.includes(permission.id)}
                  onChange={() => handlePermissionToggle(permission.id)}
                  disabled={disabled}
                />
                <label
                  htmlFor={`permission-${permission.id}`}
                  className="ml-3 text-sm cursor-pointer"
                >
                  <div className="font-medium text-gray-700">
                    {permission.name.replace(/_/g, ' ')}
                  </div>
                  {permission.description && (
                    <div className="text-gray-500">{permission.description}</div>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};